/**
 * The Billing page — authenticated subscription status, invoices, portal
 * launch, and recovery states.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C04C.
 *
 * STATUS COPY IS KEYED OFF `subscription.lifecycle`, NOT `AUTHORITY_STATES`
 * DIRECTLY — AND THAT IS A DELIBERATE RECONCILIATION, NOT A DEVIATION
 *   `BILLING_UX_SPEC.md` §5 wrote its status table against
 *   `entitlements.evaluate_authority()`'s LOCAL classifier (`valid, expired,
 *   revoked, tenant_closure, lost_device, service_outage, offline, missing,
 *   corrupt`) — the only entitlement code that existed when that spec was
 *   authored. C04B's hosted `EntitlementService.entitlement_report()` (what
 *   this page actually calls, via `components/Entitlements`) reports a
 *   different, more precise field for a browser session: `subscription
 *   .lifecycle` (`not_subscribed, incomplete, trialing, active,
 *   payment_failed, suspended, cancel_pending, ended`) — C04B's own dunning
 *   projection, which is the resolution §5 asked for
 *   ("C04B's handoff is the place that gap is either closed by extending
 *   `AUTHORITY_STATES` or resolved by clarifying that dunning grace reuses
 *   the offline grace mechanism" — C04B's handoff records it was closed a
 *   third way, by projection rather than either option, and
 *   `projects_to_authority_state` is exactly the seam back into §5's frozen
 *   vocabulary).
 *
 *   Most of `entitlements.AUTHORITY_STATES` describe LOCAL, offline
 *   conditions (`tenant_closure`, `lost_device`, `offline`,
 *   `missing`/`corrupt` document integrity) that have no meaning for an
 *   authenticated browser tab that just loaded this page over HTTPS — a
 *   session that can fetch `GET /v1/billing/entitlements` at all is, by
 *   construction, not "offline". So this page's precedence, most specific
 *   fact first:
 *
 *     1. the entitlement fetch failed outright → `Unavailable`
 *        (`CLOUD_UX_SPEC.md` §3: never looked, or the source failed to
 *        resolve);
 *     2. `report.document.revoked` → "Subscription revoked" + support link,
 *        no self-serve — §5's `revoked` row, verbatim copy;
 *     3. `!report.store_available` → §5's `service_outage` row, verbatim
 *        copy ("your access is unaffected") — this is the literal case §5
 *        describes, just reached through C04B's field name for it;
 *     4. otherwise, `subscription.lifecycle` → `LIFECYCLE_STATUS_COPY`
 *        below, which is this page's own table for the states §5 could not
 *        yet name because they did not exist in code at authoring time.
 *
 *   `tenant_closure`, `lost_device`, and `offline` have no reachable path
 *   from this page today — see the handoff's Risks section, which states
 *   this as an open gap rather than silently omitting the rows.
 *
 * PORTAL AND CHECKOUT NEVER CONSTRUCT A URL
 *   `startPortal` redirects only to `PortalHandoff.redirect_url`, exactly as
 *   the server returned it — `BILLING_UX_SPEC.md` §4: "No portal deep-link is
 *   ever constructed client-side." No `stripe.com` literal appears in this
 *   file.
 *
 * THE SUCCESS REDIRECT GRANTS NOTHING BY ARRIVING
 *   `?checkout=success` in the URL only triggers a POLL of
 *   `checkoutStatus()`, never a locally-rendered "you're subscribed". Until
 *   the server reports `granted: true`, this page shows `Pending` — see the
 *   bounded poll effect below, which is the client half of C04A's required
 *   property "client success redirect never grants entitlements without
 *   verified server state".
 *
 * OWNER WALKTHROUGH FIXES (F-31, F-32, F-33)
 *   - The recovery step for each state is a real control: "Choose a plan",
 *     "Retry checkout" and "Resubscribe" link to Pricing; "Update payment
 *     method" and "Renew" open the billing portal. "Manage billing" is shown
 *     only when there is a subscription to manage.
 *   - Copy is plain language; server codes live in a collapsed disclosure in
 *     `components/Entitlements`.
 *   - Cancellation, refund and support link to the published Refund and
 *     Cancellation Policy, the Support page and the support email, the same
 *     documents Settings → Legal links.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';

import { AuthSession, currentSession } from '../../api/auth';
import {
  BillingApiError,
  BillingState,
  CheckoutStatus,
  EntitlementReport,
  checkoutStatus,
  consumePendingCheckoutSessionRef,
  fetchBillingState,
  openBillingPortal,
} from '../../api/billing';
import { EntitlementsPanel } from '../../components/Entitlements';
import { formatMoney } from '../../lib/money';

/**
 * §5's own copy for the lifecycle states this page can actually reach. See
 * file comment. `action` says where the recovery control goes: `pricing`
 * links to plan selection, `portal` opens the billing portal.
 */
const LIFECYCLE_STATUS_COPY: Record<string, { label: string; recovery?: string; action?: 'pricing' | 'portal' }> = {
  not_subscribed: { label: "You don't have a plan yet.", recovery: 'Choose a plan', action: 'pricing' },
  incomplete: { label: 'Payment not completed', recovery: 'Retry checkout', action: 'pricing' },
  trialing: { label: 'Trial active' },
  active: { label: 'Active' },
  payment_failed: {
    label: 'Payment issue — your access continues during the recovery window',
    recovery: 'Update payment method',
    action: 'portal',
  },
  cancel_pending: { label: 'Cancelling at period end — paid access continues until then' },
  suspended: { label: 'Subscription suspended', recovery: 'Renew', action: 'portal' },
  ended: { label: 'Subscription ended', recovery: 'Resubscribe', action: 'pricing' },
};

/** The published policy and support channels (walkthrough F-31). */
const REFUND_POLICY_URL = 'https://auteurintelligence.com/legal/refunds/';
const SUPPORT_PAGE_URL = 'https://auteurintelligence.com/support/';
import { SUPPORT_EMAIL } from '../../lib/support';

function formatMinorUnits(amountMinor: number, currency: string): string {
  return formatMoney(amountMinor, currency) ?? `${(amountMinor / 100).toFixed(2)} ${currency.toUpperCase()}`;
}

function parseCheckoutQueryParam(): 'success' | 'cancelled' | null {
  const params = new URLSearchParams(window.location.search);
  const value = params.get('checkout');
  return value === 'success' || value === 'cancelled' ? value : null;
}

export function CheckoutReturnBanner({
  outcome,
  status,
  polling,
}: {
  outcome: 'success' | 'cancelled';
  status: CheckoutStatus | null;
  polling: boolean;
}) {
  if (outcome === 'cancelled') {
    return (
      <p className="billing-checkout-cancelled" role="status">
        Checkout was cancelled. Nothing changed.
      </p>
    );
  }
  if (status && status.granted) {
    return (
      <p className="billing-checkout-confirmed" role="status" data-state="Measured">
        Checkout confirmed. Your plan below is now up to date.
      </p>
    );
  }
  return (
    <p className="billing-checkout-pending" role="status" data-state="Pending">
      {status ? status.notice : 'Waiting for the provider to confirm this checkout.'}{' '}
      {polling ? 'Checking…' : null}
    </p>
  );
}

function TrialDetails({
  end,
  cancelled,
}: {
  end: string;
  cancelled: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const endMs = Date.parse(end);
  if (!end || !Number.isFinite(endMs)) {
    return (
      <p className="billing-trial-unavailable" data-state="Unavailable">
        Trial end and conversion date are unavailable. Contact support before relying on a date.
      </p>
    );
  }
  const remainingHours = Math.max(0, Math.ceil((endMs - now) / 3_600_000));
  const days = Math.floor(remainingHours / 24);
  const hours = remainingHours % 24;
  const date = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'full',
    timeStyle: 'short',
    timeZoneName: 'short',
  }).format(new Date(endMs));
  return (
    <div className="billing-trial-details" data-state="Measured">
      <p>Trial time remaining: {days} days, {hours} hours.</p>
      {cancelled ? (
        <p>Trial ends on {date}. Cancellation prevents conversion and no subscription fee is charged.</p>
      ) : (
        <p>Trial converts to paid on {date} unless cancelled before then.</p>
      )}
    </div>
  );
}

function RecoveryAction({
  copy,
  portalBusy,
  onOpenPortal,
}: {
  copy: { recovery?: string; action?: 'pricing' | 'portal' };
  portalBusy: boolean;
  onOpenPortal: () => void;
}) {
  if (!copy.recovery) return null;
  if (copy.action === 'pricing') {
    return (
      <p className="billing-status-recovery">
        <Link href="/pricing" className="button-link">
          {copy.recovery}
        </Link>
      </p>
    );
  }
  if (copy.action === 'portal') {
    return (
      <p className="billing-status-recovery">
        <button type="button" disabled={portalBusy} onClick={onOpenPortal}>
          {copy.recovery}
        </button>
      </p>
    );
  }
  return <p className="billing-status-recovery">{copy.recovery}</p>;
}

export function StatusBanner({
  billingState,
  entitlement,
  portalBusy,
  onOpenPortal,
}: {
  billingState: BillingState | null;
  entitlement: EntitlementReport | null;
  portalBusy: boolean;
  onOpenPortal: () => void;
}) {
  if (!entitlement) {
    return (
      <div className="billing-status-banner" role="alert" data-state="Unavailable">
        <p>Billing status is unavailable right now.</p>
      </div>
    );
  }
  if (entitlement.document && entitlement.document.revoked) {
    return (
      <div className="billing-status-banner" data-state="revoked">
        <p>Subscription revoked.</p>
        <p className="billing-status-support">
          This is not self-serve — see Support below.
        </p>
      </div>
    );
  }
  if (!entitlement.store_available) {
    return (
      <div className="billing-status-banner" role="status" data-state="service_outage">
        <p>Billing service temporarily unavailable — your access is unaffected.</p>
      </div>
    );
  }
  const lifecycle = entitlement.subscription.lifecycle;
  const copy = LIFECYCLE_STATUS_COPY[lifecycle] || { label: lifecycle };
  const trialing = lifecycle === 'trialing';
  const cancellingTrial = Boolean(
    billingState && billingState.status === 'trialing' && billingState.cancel_at_period_end,
  );
  const trialEnd = entitlement.subscription.entitlement_ends_at;
  const trialEndMatchesBilling = Boolean(
    billingState && billingState.status === 'trialing' &&
    billingState.trial_end && Date.parse(billingState.trial_end) === Date.parse(trialEnd),
  );
  return (
    <div className="billing-status-banner" data-state={lifecycle}>
      <p>{copy.label}</p>
      <RecoveryAction copy={copy} portalBusy={portalBusy} onOpenPortal={onOpenPortal} />
      {trialing ? (
        <TrialDetails end={trialEndMatchesBilling ? trialEnd : ''} cancelled={cancellingTrial} />
      ) : null}
      {billingState && billingState.subscribed && billingState.cancel_at_period_end ? (
        <p className="billing-status-cancel-notice">
          {cancellingTrial
            ? 'Trial cancellation is scheduled; it will not convert to a paid subscription.'
            : 'Set to cancel at the end of the current period.'}
        </p>
      ) : null}
    </div>
  );
}

function InvoicesList({ billingState }: { billingState: BillingState | null }) {
  if (!billingState || billingState.invoices.length === 0) {
    return <p className="billing-invoices-empty">No invoices yet.</p>;
  }
  return (
    <table className="billing-invoices">
      <thead>
        <tr>
          <th>Status</th>
          <th>Amount due</th>
          <th>Amount paid</th>
          <th>Period</th>
        </tr>
      </thead>
      <tbody>
        {billingState.invoices.map((invoice, index) => (
          // Invoices carry no id this API returns (deliberately — see
          // `api/billing.ts`); position is stable for one render of one fetch.
          <tr key={index} data-status={invoice.status}>
            <td>{invoice.status}</td>
            <td>{formatMinorUnits(invoice.amount_due_minor, invoice.currency)}</td>
            <td>{formatMinorUnits(invoice.amount_paid_minor, invoice.currency)}</td>
            <td>
              {invoice.period_start || '—'} – {invoice.period_end || '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Cancellation, refund, and support terms — links only, never inline policy
 * copy. `BILLING_UX_SPEC.md` §7: "This UI never states a cancellation or
 * refund policy inline". The policy is now published (Refund and
 * Cancellation Policy, version 2026-10-02, also linked from Settings → Legal),
 * so this section links it and the published support channels (F-31).
 */
export function PolicyLinks(): React.ReactElement {
  return (
    <section className="billing-policy-links">
      <h2>Cancellation, refunds, and support</h2>
      <p data-state="Published">
        <a href={REFUND_POLICY_URL} target="_blank" rel="noopener noreferrer">
          Refund and Cancellation Policy
        </a>
      </p>
      <p data-state="Published">
        Support:{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        {' · '}
        <a href={SUPPORT_PAGE_URL} target="_blank" rel="noopener noreferrer">
          Support page
        </a>
      </p>
    </section>
  );
}

export interface BillingPageProps {
  /** Defaults to navigating this tab to `/login?next=/billing`. */
  onRequireLogin?: () => void;
}

export function BillingPage({ onRequireLogin }: BillingPageProps = {}): React.ReactElement {
  const [session, setSession] = useState<AuthSession | null | undefined>(undefined);
  const [billingState, setBillingState] = useState<BillingState | null>(null);
  const [billingUnavailable, setBillingUnavailable] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [entitlement, setEntitlement] = useState<EntitlementReport | null>(null);
  const [portalError, setPortalError] = useState('');
  const [portalBusy, setPortalBusy] = useState(false);

  const [checkoutOutcome] = useState<'success' | 'cancelled' | null>(parseCheckoutQueryParam);
  const [checkoutStatusResult, setCheckoutStatusResult] = useState<CheckoutStatus | null>(null);
  const [checkoutPolling, setCheckoutPolling] = useState(false);
  const pollAttempts = useRef(0);

  const refreshBillingState = useCallback(async () => {
    try {
      const state = await fetchBillingState();
      setBillingState(state);
      setBillingUnavailable(false);
    } catch (failure) {
      setBillingState(null);
      setBillingUnavailable(true);
      setBillingError(
        failure instanceof BillingApiError ? failure.message : 'billing state could not be reached',
      );
    }
  }, []);

  useEffect(() => {
    currentSession().then(setSession);
  }, []);

  useEffect(() => {
    if (session === null && onRequireLogin) {
      onRequireLogin();
    }
  }, [session, onRequireLogin]);

  useEffect(() => {
    if (session) {
      refreshBillingState();
    }
  }, [session, refreshBillingState]);

  // Poll the checkout outcome — bounded, and it grants nothing itself. See
  // the file comment: this is the client half of C04A's required property.
  useEffect(() => {
    if (!session || checkoutOutcome !== 'success') return;
    const sessionRef = consumePendingCheckoutSessionRef();
    if (!sessionRef) return;

    let cancelled = false;
    const MAX_ATTEMPTS = 5;
    const poll = async () => {
      if (cancelled) return;
      setCheckoutPolling(true);
      try {
        const status = await checkoutStatus(sessionRef);
        if (cancelled) return;
        setCheckoutStatusResult(status);
        pollAttempts.current += 1;
        if (!status.granted && pollAttempts.current < MAX_ATTEMPTS) {
          setTimeout(poll, 3000);
          return;
        }
        if (status.granted) {
          await refreshBillingState();
        }
      } catch {
        // A stale or foreign session_ref reads as "unknown" server-side; this
        // page does not guess at what happened, it just stops polling.
      } finally {
        if (!cancelled) setCheckoutPolling(false);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, checkoutOutcome]);

  const startPortal = useCallback(() => {
    setPortalBusy(true);
    setPortalError('');
    openBillingPortal('/billing')
      .then((handoff) => {
        window.location.assign(handoff.redirect_url);
      })
      .catch((failure) => {
        setPortalBusy(false);
        setPortalError(
          failure instanceof BillingApiError ? failure.message : 'the billing portal could not be opened',
        );
      });
  }, []);

  // With no subscription there is nothing in the portal to manage (F-33), so
  // the plan button above is the only call to action. While either source is
  // still unknown, keep the portal available rather than guess.
  const hasSubscriptionToManage = !(
    entitlement &&
    entitlement.subscription.lifecycle === 'not_subscribed' &&
    !(billingState && billingState.subscribed)
  );

  if (session === undefined) {
    return <p className="billing-loading">Loading…</p>;
  }
  if (session === null) {
    return <p className="billing-signed-out">Sign in to see your billing.</p>;
  }

  return (
    <section className="billing-page">
      <header>
        <h1>Billing</h1>
        <p className="billing-scope">
          Pay for Auteur: your plan, invoices and payment method. This is not where your own customers pay
          you; payment links for your storefront are set up in your workspace under Launch.
        </p>
      </header>

      {checkoutOutcome ? (
        <CheckoutReturnBanner
          outcome={checkoutOutcome}
          status={checkoutStatusResult}
          polling={checkoutPolling}
        />
      ) : null}

      <StatusBanner
        billingState={billingState}
        entitlement={entitlement}
        portalBusy={portalBusy}
        onOpenPortal={startPortal}
      />

      {portalError ? (
        <p className="billing-portal-error" role="alert">
          {portalError}
        </p>
      ) : null}
      {hasSubscriptionToManage ? (
        <button type="button" disabled={portalBusy} onClick={startPortal}>
          Manage billing
        </button>
      ) : null}

      <h2>Invoices</h2>
      {billingUnavailable ? (
        <p className="billing-invoices-unavailable" role="alert" data-state="Unavailable">
          Invoices are unavailable right now: {billingError}
        </p>
      ) : (
        <InvoicesList billingState={billingState} />
      )}

      <EntitlementsPanel onReport={setEntitlement} />

      <PolicyLinks />
    </section>
  );
}

export default BillingPage;
